import { io } from 'socket.io-client';
import { getCurrentVoiceRetailerRoomId, getCurrentVoiceRetailerUserId, getVoicePlatformSocketUrl } from '../config/voiceEnvConfig';
import {
  buildVoiceJoinRoomPayload,
  buildVoiceSocketAuthPayload
} from '../auth/voiceAuth';
import { DEFAULT_VOICE_SOCKET_EVENTS, VOICE_SOCKET_EVENTS } from '../../domain/voiceEventConstants';
import {
  isVoiceSocketDebugEnabled,
  voiceSocketLog,
  voiceSocketWarn,
  voiceSocketError
} from '../../../../utils/voiceSocketDebug';

/** @typedef {import('socket.io-client').Socket} VoiceSocket */

/** @type {VoiceSocket|null} */
let voiceSocketInstance = null;

/** Voice Socket.IO room = String(retailerId). */
let currentVoiceRoomId = '';

/** Agent identity sent with join_room / auth. Not used as the room. */
let currentVoiceRetailerUserId = '';

/** @type {Set<string>} */
const registeredEvents = new Set();

/** @type {Map<string, Set<(payload: unknown) => void>>} */
const externalHandlersByEvent = new Map();

/** @type {WeakSet<VoiceSocket>} */
const lifecycleDebugSockets = new WeakSet();

/** Invalidates in-flight join_room if connect/room changes while JWT is resolving. */
let joinRoomSeq = 0;

function applyVoiceSocketExtraHeaders(token) {
  const extraHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  if (voiceSocketInstance?.io?.opts) {
    voiceSocketInstance.io.opts.extraHeaders = extraHeaders;
  }
}

/**
 * Socket.IO handshake callback — re-runs on every reconnect so the Mercately JWT stays fresh.
 *
 * @param {(auth: Record<string, unknown>) => void} cb
 */
function applyVoiceSocketAuth(cb) {
  buildVoiceSocketAuthPayload()
    .then((auth) => {
      if (!auth.token) {
        voiceSocketWarn('handshake without Mercately JWT — voice-service will disconnect');
      }
      applyVoiceSocketExtraHeaders(auth.token);
      cb(auth);
    })
    .catch((error) => {
      voiceSocketError('failed to resolve Mercately JWT for handshake', error);
      applyVoiceSocketExtraHeaders('');
      cb({});
    });
}

async function emitVoiceJoinRoom() {
  if (!voiceSocketInstance?.connected) return;

  const seq = joinRoomSeq + 1;
  joinRoomSeq = seq;

  let payload;
  try {
    payload = await buildVoiceJoinRoomPayload(currentVoiceRoomId);
  } catch (error) {
    voiceSocketError('join_room payload failed', error);
    return;
  }

  if (seq !== joinRoomSeq) return;
  if (!voiceSocketInstance?.connected) return;

  if (payload.room) {
    currentVoiceRoomId = payload.room;
  }
  if (!payload.room) {
    voiceSocketWarn('join_room skipped — missing retailer_id in JWT/session');
    return;
  }

  voiceSocketInstance.emit(VOICE_SOCKET_EVENTS.JOIN_ROOM, payload, (ack) => {
    voiceSocketLog('join_room ack', ack);
    // eslint-disable-next-line no-console
    console.info('[VoiceSocket] join_room ack', ack);
  });
  // eslint-disable-next-line no-console
  console.info('[VoiceSocket] join_room', { ...payload, socketId: voiceSocketInstance.id });
}

/**
 * Subscribe this tab to the retailer's voice room. Re-emit on every Socket.IO `connect`.
 * Room is always retailerId. retailer_user_id is the agent, not the room.
 *
 * @param {string|number|null|undefined} retailerId
 * @param {string|number|null|undefined} [retailerUserId]
 */
export function setVoiceJoinRoomId(retailerId, retailerUserId) {
  if (retailerUserId != null && retailerUserId !== '') {
    currentVoiceRetailerUserId = String(retailerUserId);
  }

  const room = retailerId == null || retailerId === '' ? '' : String(retailerId);
  if (!room) {
    currentVoiceRoomId = '';
    return;
  }

  const roomChanged = Boolean(currentVoiceRoomId) && currentVoiceRoomId !== room;
  currentVoiceRoomId = room;

  if (voiceSocketInstance) {
    voiceSocketInstance.auth = applyVoiceSocketAuth;
  }

  if (roomChanged && voiceSocketInstance) {
    voiceSocketLog('retailer room changed — reconnecting', {
      room,
      retailer_user_id: currentVoiceRetailerUserId
    });
    voiceSocketInstance.disconnect();
    voiceSocketInstance.connect();
    return;
  }

  emitVoiceJoinRoom();
}

/**
 * Re-emit join_room with the current tab_session_id (e.g. after rotating the tab SIP identity).
 * @returns {Promise<void>}
 */
export function refreshVoiceJoinRoom() {
  return emitVoiceJoinRoom();
}

/**
 * @param {VoiceSocket} socket
 */
function attachVoiceSocketLifecycleDebug(socket) {
  if (lifecycleDebugSockets.has(socket)) return;

  lifecycleDebugSockets.add(socket);

  socket.on('connect', () => {
    // eslint-disable-next-line no-console
    console.info('[VoiceSocket] connect', { socketId: socket.id, connected: socket.connected });
    emitVoiceJoinRoom();
  });

  socket.on('disconnect', (reason) => {
    // eslint-disable-next-line no-console
    console.info('[VoiceSocket] disconnect', { reason, connected: socket.connected });
  });

  socket.on('connect_error', (error) => {
    // eslint-disable-next-line no-console
    console.error('[VoiceSocket] connect_error', error?.message ?? error);
  });

  socket.io.on('reconnect_attempt', (attempt) => {
    voiceSocketLog('reconnect_attempt', { attempt });
  });

  socket.io.on('reconnect', (attempt) => {
    voiceSocketLog('reconnect', { attempt, socketId: socket.id });
  });

  socket.io.on('reconnect_error', (error) => {
    voiceSocketError('reconnect_error', error?.message ?? error);
  });

  socket.io.on('reconnect_failed', () => {
    voiceSocketError('reconnect_failed');
  });
}

/**
 * @param {string} eventName
 * @param {(payload: unknown) => void} handler
 */
function addExternalHandler(eventName, handler) {
  if (!externalHandlersByEvent.has(eventName)) {
    externalHandlersByEvent.set(eventName, new Set());
  }
  externalHandlersByEvent.get(eventName)?.add(handler);
}

/**
 * @param {string} eventName
 * @param {(payload: unknown) => void} handler
 */
function removeExternalHandler(eventName, handler) {
  externalHandlersByEvent.get(eventName)?.delete(handler);
}

/**
 * @param {string} eventName
 * @param {unknown} payload
 */
function dispatchToExternalHandlers(eventName, payload) {
  const handlers = externalHandlersByEvent.get(eventName);
  if (!handlers) return;

  voiceSocketLog('event', eventName, payload);

  handlers.forEach((handler) => {
    try {
      handler(payload);
    } catch (error) {
      voiceSocketError(`handler error (${eventName})`, error);
    }
  });
}

/**
 * @param {string} eventName
 */
function ensureInternalListener(eventName) {
  if (!voiceSocketInstance || registeredEvents.has(eventName)) return;

  const internalHandler = (payload) => {
    dispatchToExternalHandlers(eventName, payload);
  };

  voiceSocketInstance.on(eventName, internalHandler);
  registeredEvents.add(eventName);
  voiceSocketInstance[`__voiceInternal_${eventName}`] = internalHandler;

  voiceSocketLog('listener registered', eventName);
}

/**
 * @param {string[]} events
 */
function registerEvents(events) {
  events.forEach((eventName) => ensureInternalListener(eventName));
}

/**
 * Reusable Voice Platform socket initializer (namespace `/voice`, engine path `/socket.io`).
 * Does not use the `/notifications` socket.
 *
 * @param {Object} options
 * @param {string[]} [options.events]
 * @param {string|number} [options.retailerId]
 * @param {string|number} [options.retailerUserId]
 * @returns {VoiceSocket|null}
 */
export function initializeVoiceSocket(options = {}) {
  const { events = DEFAULT_VOICE_SOCKET_EVENTS, retailerId, retailerUserId } = options;
  const socketUrl = getVoicePlatformSocketUrl();

  if (!socketUrl) {
    // eslint-disable-next-line no-console
    console.warn(
      '[VoiceSocket] ENV.VOICE_PLATFORM_SOCKET_URL / VOICE_PLATFORM_API_URL is not configured — socket not initialized'
    );
    return null;
  }

  if (/notifications/i.test(socketUrl)) {
    voiceSocketWarn('Voice socket URL looks like /notifications — use /voice (VOICE_PLATFORM_SOCKET_URL)');
  }

  const resolvedRetailerId = retailerId ?? getCurrentVoiceRetailerRoomId();
  const resolvedRetailerUserId = retailerUserId ?? getCurrentVoiceRetailerUserId();
  if (resolvedRetailerUserId) {
    currentVoiceRetailerUserId = String(resolvedRetailerUserId);
  }

  if (!voiceSocketInstance) {
    if (resolvedRetailerId) {
      currentVoiceRoomId = String(resolvedRetailerId);
    }

    // eslint-disable-next-line no-console
    console.info('[VoiceSocket] initializing client', {
      url: socketUrl,
      path: '/socket.io',
      events,
      room: currentVoiceRoomId,
      retailer_user_id: currentVoiceRetailerUserId
    });

    voiceSocketInstance = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      autoConnect: true,
      extraHeaders: {},
      auth: applyVoiceSocketAuth
    });

    attachVoiceSocketLifecycleDebug(voiceSocketInstance);
  } else {
    setVoiceJoinRoomId(resolvedRetailerId, resolvedRetailerUserId);
    voiceSocketLog('reusing existing socket instance', {
      connected: voiceSocketInstance.connected,
      socketId: voiceSocketInstance.id,
      room: currentVoiceRoomId,
      retailer_user_id: currentVoiceRetailerUserId
    });
  }

  registerEvents(events);

  if (isVoiceSocketDebugEnabled()) {
    voiceSocketLog('state', {
      connected: voiceSocketInstance.connected,
      registeredEvents: [...registeredEvents],
      subscriberCounts: Object.fromEntries(
        [...externalHandlersByEvent.entries()].map(([name, set]) => [name, set.size])
      )
    });
  }

  return voiceSocketInstance;
}

/**
 * @returns {VoiceSocket|null}
 */
export function getVoiceSocketInstance() {
  return voiceSocketInstance;
}

/**
 * Subscribe to a Voice Platform socket event (supports multiple subscribers).
 *
 * @param {string} eventName
 * @param {(payload: unknown) => void} handler
 * @returns {() => void} Unsubscribe function
 */
export function subscribeVoiceSocketEvent(eventName, handler) {
  addExternalHandler(eventName, handler);
  ensureInternalListener(eventName);

  voiceSocketLog('subscribed', eventName);

  return () => {
    removeExternalHandler(eventName, handler);
    voiceSocketLog('unsubscribed', eventName);
  };
}

/**
 * Disconnect and reset the Voice Platform socket singleton.
 */
export function teardownVoiceSocket() {
  if (!voiceSocketInstance) return;

  voiceSocketLog('teardown');

  registeredEvents.forEach((eventName) => {
    const internalHandler = voiceSocketInstance?.[`__voiceInternal_${eventName}`];
    if (internalHandler) {
      voiceSocketInstance?.off(eventName, internalHandler);
      delete voiceSocketInstance[`__voiceInternal_${eventName}`];
    }
  });

  registeredEvents.clear();
  externalHandlersByEvent.clear();
  currentVoiceRoomId = '';
  currentVoiceRetailerUserId = '';
  joinRoomSeq += 1;
  voiceSocketInstance.disconnect();
  voiceSocketInstance = null;
}

/**
 * @returns {boolean}
 */
export function isVoiceSocketConnected() {
  return Boolean(voiceSocketInstance?.connected);
}
